import React from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import CustomSwapWidget from '../../swap/CustomSwapWidget';

interface TradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  // The token to trade (pre-populated as output/destination)
  token: {
    address: string;
    symbol: string;
    name: string;
    chainId?: number;
    logo?: string | null;
    decimals?: number;
  } | null;
}

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #0a0a0a;
  overflow: hidden;
  
  @media (max-width: 768px) {
    height: 100vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(168, 85, 247, 0.2);
  background: #0a0a0a;
`;

const ModalTitle = styled.h2`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TokenBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(168, 85, 247, 0.15);
  border: 1px solid rgba(168, 85, 247, 0.3);
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 13px;
  color: #a855f7;
`;

const TokenLogo = styled.img`
  width: 18px;
  height: 18px;
  border-radius: 50%;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 18px;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
    border-color: rgba(168, 85, 247, 0.3);
  }
  
  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    font-size: 24px;
  }
`;

const SwapContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;
  
  /* Hide the header in the swap widget since we have our own */
  & > div > div:first-child {
    /* Keep the swap widget header visible for settings */
  }
`;

/**
 * Trade Modal - Opens the CustomSwapWidget with a pre-populated output token
 * When user clicks "Trade" on a token in the explore section, this modal opens
 * with that token as the output currency (what they want to buy)
 */
const TradeModal: React.FC<TradeModalProps> = ({
  isOpen,
  onClose,
  token
}) => {
  // Convert token to the format expected by CustomSwapWidget
  const preselectedToken = token ? {
    address: token.address,
    symbol: token.symbol,
    name: token.name,
    chainId: token.chainId || 57073, // Default to Ink chain
    logo: token.logo || undefined,
    decimals: token.decimals || 18,
    balance: '0',
    priceUSD: undefined,
  } : null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Trade Token"
      ariaHideApp={false}
      style={{
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          zIndex: 1000,
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: typeof window !== 'undefined' && window.innerWidth <= 768 ? 'flex-end' : 'center',
          justifyContent: 'center',
          padding: typeof window !== 'undefined' && window.innerWidth <= 768 ? '0' : '20px',
        },
        content: {
          width: typeof window !== 'undefined' && window.innerWidth <= 768 ? '100%' : '420px',
          height: typeof window !== 'undefined' && window.innerWidth <= 768 ? '100vh' : 'auto',
          maxWidth: typeof window !== 'undefined' && window.innerWidth <= 768 ? '100vw' : '95vw',
          maxHeight: typeof window !== 'undefined' && window.innerWidth <= 768 ? '100vh' : '90vh',
          margin: typeof window !== 'undefined' && window.innerWidth <= 768 ? '0' : 'auto',
          borderRadius: typeof window !== 'undefined' && window.innerWidth <= 768 ? '0' : '16px',
          background: '#0a0a0a',
          border: typeof window !== 'undefined' && window.innerWidth <= 768 ? 'none' : '1px solid rgba(168, 85, 247, 0.2)',
          padding: 0,
          inset: typeof window !== 'undefined' && window.innerWidth <= 768 ? '0' : 'auto',
          position: typeof window !== 'undefined' && window.innerWidth <= 768 ? 'fixed' : 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: typeof window !== 'undefined' && window.innerWidth <= 768 ? 'none' : '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(168, 85, 247, 0.1)'
        },
      }}
    >
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>
            Trade
            {token && (
              <TokenBadge>
                {token.logo && <TokenLogo src={token.logo} alt={token.symbol} />}
                ${token.symbol}
              </TokenBadge>
            )}
          </ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>
        
        <SwapContainer>
          <CustomSwapWidget 
            preselectedToToken={preselectedToken}
            embedded={true}
          />
        </SwapContainer>
      </ModalContainer>
    </Modal>
  );
};

export default TradeModal;

